import { LayoutDashboard, CreditCard, Truck, Users, Receipt, MoreHorizontal, TrendingUp, Navigation, Sliders, LogOut, ChevronRight, ArrowLeft } from 'lucide-react';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './index.css';

// Public & Auth Views
import LandingPage from './components/LandingPage';
import TenantLogin from './components/TenantLogin';
import PublicInvoiceView from './components/PublicInvoiceView';
import PublicStatementView from './components/PublicStatementView';
import DeveloperPortal from './components/DeveloperPortal';

// Main App Layout Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';

// Feature Modules
import Dashboard from './components/Dashboard';
import CounterPOS from './components/CounterPOS';
import DailyRunSheet from './components/DailyRunSheet';
import CustomerManagement from './components/CustomerManagement';
import ProductCatalog from './components/ProductCatalog';
import BillingLedger from './components/BillingLedger';
import CustomerPaymentReceipts from './components/CustomerPaymentReceipts';
import LinesMaster from './components/LinesMaster';
import RateChartMaster from './components/RateChartMaster';
import UserManagement from './components/UserManagement';
import OnlineOrders from './components/OnlineOrders';
import PWAInstallPrompt from './components/PWAInstallPrompt';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

function parseHash() {
  const raw = window.location.hash.replace(/^#\/?/, '').toLowerCase();
  if (raw.startsWith('statement-view')) {
    return { view: 'statement-view', slug: null };
  }
  if (raw.startsWith('invoice-view')) {
    return { view: 'invoice-view', slug: null };
  }
  if (raw === 'developer' || raw === 'developer_portal') {
    return { view: 'developer', slug: null };
  }
  if (raw === 'landing') {
    return { view: 'landing', slug: null };
  }

  // 1. Check if hash has a tenant slug (?slug=sachin-milk-shop)
  if (raw.includes('slug=')) {
    const match = raw.match(/slug=([^&]+)/);
    if (match && match[1]) {
      try { localStorage.setItem('milkpro_last_tenant_slug', match[1]); } catch (e) {}
      return { view: 'login', slug: match[1] };
    }
  }

  // 2. If dedicated dairy slug is in hash (e.g. #sachin-milk-shop)
  if (raw && raw !== 'login' && raw !== 'app') {
    const cleanSlug = raw.replace('/login', '').replace('login/', '');
    try { localStorage.setItem('milkpro_last_tenant_slug', cleanSlug); } catch (e) {}
    return { view: 'login', slug: cleanSlug };
  }

  // 3. When opened from mobile home screen / PWA launch (hash is "#app", "" or "#"):
  // A. If already logged in, enter Dairy App Dashboard directly!
  try {
    const savedUser = localStorage.getItem('milkpro_tenant_user');
    const savedTenant = localStorage.getItem('milkpro_tenant_data');
    if (savedUser && savedTenant) {
      return { view: 'app', slug: null };
    }
  } catch (e) {}

  // B. If not logged in, but we have a remembered tenant slug, go to that dairy's login directly!
  try {
    const savedSlug = localStorage.getItem('milkpro_last_tenant_slug');
    if (savedSlug) {
      return { view: 'login', slug: savedSlug };
    }
  } catch (e) {}

  // C. Fallback to landing page for general visitors with no tenant link
  return { view: 'landing', slug: null };
}

export default function App() {
  const [lang, setLang] = useState('mr'); // 'mr' (Marathi) or 'en' (English)
  const isMr = lang === 'mr';

  // Navigation View: 'landing', 'login', 'app', 'developer'
  const [routeState, setRouteState] = useState(parseHash);

  // Logged-in Tenant & User state from localStorage
  const [tenantUser, setTenantUser] = useState(() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [currentTenant, setCurrentTenant] = useState(() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // UI States within the Dairy App
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_user');
      const u = saved ? JSON.parse(saved) : null;
      if (u?.role === 'DELIVERY') return 'runsheet';
      if (u?.role === 'CASHIER') return 'pos';
    } catch {}
    return 'dashboard';
  });
  const tenantFeatures = useMemo(() => {
    return currentTenant?.features || currentTenant?.feature || {};
  }, [currentTenant]);

  const showProductCatalog = useMemo(() => {
    return Boolean(tenantFeatures?.counter_pos || tenantFeatures?.dairy_byproducts || tenantFeatures?.bakery_products);
  }, [tenantFeatures]);

  // Safeguard: redirect to dashboard if active tab is disabled by tenant features
  useEffect(() => {
    if (activeTab === 'pos' && tenantFeatures.counter_pos === false) {
      setActiveTab('dashboard');
    } else if (activeTab === 'runsheet' && tenantFeatures.daily_runsheet === false) {
      setActiveTab('dashboard');
    } else if (activeTab === 'online_orders' && tenantFeatures.online_orders === false) {
      setActiveTab('dashboard');
    } else if (activeTab === 'products' && !showProductCatalog) {
      setActiveTab(tenantFeatures.daily_runsheet !== false ? 'runsheet' : 'dashboard');
    }
  }, [activeTab, tenantFeatures, showProductCatalog]);
  const [shift, setShift] = useState('Morning');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [gstEnabled, setGstEnabled] = useState(true);

  // Business Data States - 100% Live DB State (ZERO MOCK FALLBACKS)
  const [centers, setCenters] = useState([]);
  const [lines, setLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [deliveriesToday, setDeliveriesToday] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dairySettings, setDairySettings] = useState(() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? (JSON.parse(saved)?.dairy_settings || {}) : {};
    } catch {
      return {};
    }
  });

  // Active Tenant Slug
  const activeSlug = currentTenant?.slug || (routeState.view === 'login' ? routeState.slug : 'samarth-krupa-dairy');

  // Master Loader for Live Tenant Data
  const loadTenantData = useCallback(async (slug) => {
    if (!slug) return;
    setDataLoading(true);
    try {
      // 1. Centers & Lines
      const clRes = await fetch(`${API_BASE}/tenant/${slug}/centers-lines`);
      const clData = await clRes.json();
      if (clData.success) {
        setCenters(clData.centers || []);
        setLines(clData.lines || []);
      }

      // 2. Products
      const prodRes = await fetch(`${API_BASE}/tenant/${slug}/products`);
      const prodData = await prodRes.json();
      if (prodData.success) {
        setProducts(prodData.products || []);
      }

      // 3. Customers
      const custRes = await fetch(`${API_BASE}/tenant/${slug}/customers`);
      const custData = await custRes.json();
      if (custData.success) {
        setCustomers(custData.customers || []);
      }

      // 4. Deliveries for today and shift
      const delRes = await fetch(`${API_BASE}/tenant/${slug}/deliveries?date=${selectedDate}&shift=${shift}`);
      const delData = await delRes.json();
      if (delData.success) {
        setDeliveriesToday(delData.deliveries || []);
      }

      // 5. Online Orders
      const ordRes = await fetch(`${API_BASE}/tenant/${slug}/orders`);
      const ordData = await ordRes.json();
      if (ordData.success) {
        setOnlineOrders(ordData.orders || []);
      }

      // 6. Dairy Profile Settings from Tenant DB
      try {
        const setRes = await fetch(`${API_BASE}/tenant/${slug}/settings/profile`);
        const setData = await setRes.json();
        if (setData.success && setData.settings) {
          setDairySettings(setData.settings);
          setCurrentTenant(prev => ({
            ...prev,
            dairy_settings: setData.settings,
            feature: prev?.feature || setData.tenant?.feature,
            plan: prev?.plan || setData.tenant?.plan
          }));
        }
      } catch (err) {
        console.warn('Could not load dairy settings:', err);
      }
    } catch (err) {
      console.error('Error loading live tenant data:', err);
    } finally {
      setDataLoading(false);
    }
  }, [selectedDate, shift]);

  // Load data when slug, shift or date changes
  useEffect(() => {
    if (activeSlug && (routeState.view === 'app' || tenantUser)) {
      loadTenantData(activeSlug);
    }
  }, [activeSlug, routeState.view, tenantUser, loadTenantData]);

  // Also resolve current tenant details from landlord DB if missing
  useEffect(() => {
    if (!currentTenant && activeSlug) {
      fetch(`${API_BASE}/tenant/by-slug/${activeSlug}`)
        .then(r => r.json())
        .then(d => {
          if (d.success && d.tenant) {
            setCurrentTenant(d.tenant);
            if (d.settings) setDairySettings(d.settings);
            localStorage.setItem('milkpro_tenant_data', JSON.stringify(d.tenant));
          }
        })
        .catch(err => console.error('Tenant fetch error:', err));
    }
  }, [currentTenant, activeSlug]);

  // Sync settings when entering settings tab
  useEffect(() => {
    if (activeTab === 'settings' && activeSlug) {
      fetch(`${API_BASE}/tenant/${activeSlug}/settings/profile`)
        .then(r => r.json())
        .then(d => {
          if (d.success && d.settings) {
            setDairySettings(d.settings);
            if (d.tenant) {
              setCurrentTenant(prev => ({
                ...prev,
                ...d.tenant,
                feature: d.tenant.feature || prev?.feature,
                plan: d.tenant.plan || prev?.plan,
                dairy_settings: d.settings
              }));
            }
          }
        })
        .catch(err => console.warn('Settings load error:', err));
    }
  }, [activeTab, activeSlug]);

  // Sync with URL Hash dynamically
  useEffect(() => {
    const handleHashChange = () => {
      setRouteState(parseHash());
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigateTo = (target) => {
    if (target === 'landing') {
      window.location.hash = '#landing';
    } else if (target === 'login') {
      window.location.hash = `#${activeSlug || 'samarth-krupa-dairy'}`;
    } else if (target === 'developer') {
      window.location.hash = '#developer';
    } else if (target === 'app') {
      window.location.hash = '#app';
    } else if (target.startsWith('tenant_login_')) {
      const slug = target.replace('tenant_login_', '');
      window.location.hash = `#${slug}`;
    }
  };

  // Handle successful tenant login
  const handleTenantLoginSuccess = (user, tenant) => {
    setTenantUser(user);
    setCurrentTenant(tenant);
    loadTenantData(tenant.slug);
    if (user.role === 'CASHIER') setActiveTab('pos');
    else if (user.role === 'DELIVERY') setActiveTab('runsheet');
    else setActiveTab('dashboard');
    navigateTo('app');
  };

  // Handle Tenant Logout (supports concurrent session notification)
  const handleTenantLogout = (customMsg) => {
    localStorage.removeItem('milkpro_tenant_token');
    localStorage.removeItem('milkpro_tenant_user');
    localStorage.removeItem('milkpro_tenant_data');
    localStorage.removeItem('milkpro_session_token');
    if (customMsg && typeof customMsg === 'string') {
      localStorage.setItem('milkpro_concurrent_logout_msg', customMsg);
    }
    setTenantUser(null);
    const lastSlug = localStorage.getItem('milkpro_last_tenant_slug') || currentTenant?.slug;
    if (lastSlug) {
      navigateTo('login', lastSlug);
    } else {
      navigateTo('landing');
    }
  };

  // Periodic heartbeat: Check single active device session
  useEffect(() => {
    if (!tenantUser || !currentTenant?.slug) return;
    const sessionToken = localStorage.getItem('milkpro_session_token');
    if (!sessionToken) return;

    const checkSession = async () => {
      try {
        const token = localStorage.getItem('milkpro_tenant_token');
        const res = await fetch(`${API_BASE}/tenant/${encodeURIComponent(currentTenant.slug)}/validate-session?user_id=${tenantUser.id}&session_token=${encodeURIComponent(sessionToken)}`, {
          headers: {
            'X-Session-Token': sessionToken,
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        const data = await res.json();
        if (data && data.session_terminated) {
          handleTenantLogout(data.message || (isMr ? '⚠️ दुसऱ्या डिव्हाइसवरून लॉगिन झाल्यामुळे हे सत्र आपोआप लॉगआउट झाले आहे.' : 'Logged out because another device logged into this account.'));
        }
      } catch (e) {}
    };

    const initialTimer = setTimeout(checkSession, 4000);
    const interval = setInterval(checkSession, 12000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [tenantUser, currentTenant, isMr]);

  // Customer Mutators (Live Synced with DB)
  const handleSaveCustomer = (cust) => {
    setCustomers(prev => {
      const exists = prev.some(c => c.id === cust.id);
      if (exists) return prev.map(c => c.id === cust.id ? cust : c);
      return [...prev, cust];
    });
  };

  const handleDeleteCustomer = (id) => {
    setCustomers(prev => prev.filter(c => c.id !== id));
  };

  const handleMarkVacation = (id, newStatus) => {
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
  };

  // Product Mutators
  const handleSaveProduct = (prod) => {
    setProducts(prev => {
      const exists = prev.some(p => p.id === prod.id);
      if (exists) return prev.map(p => p.id === prod.id ? prod : p);
      return [...prev, prod];
    });
  };

  const handleDeleteProduct = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  // Line Mutators
  const handleSaveLine = (line) => {
    setLines(prev => {
      const exists = prev.some(l => l.id === line.id);
      if (exists) return prev.map(l => l.id === line.id ? line : l);
      return [...prev, line];
    });
  };

  const handleDeleteLine = (id) => {
    setLines(prev => prev.filter(l => l.id !== id));
  };

  // Deliveries Mutator
  const handleUpdateDelivery = (custId, updated) => {
    if (!custId || !updated) return;
    setDeliveriesToday(prev => {
      const idx = prev.findIndex(d => (d.customer_id || d.customerId) === custId);
      const cQty = updated.cowQty !== undefined ? updated.cowQty : (updated.cow_qty !== undefined ? updated.cow_qty : 0);
      const bQty = updated.bufQty !== undefined ? updated.bufQty : (updated.buf_qty !== undefined ? updated.buf_qty : 0);
      const normalized = {
        ...updated,
        customer_id: custId,
        cow_qty: cQty,
        cowQty: cQty,
        buf_qty: bQty,
        bufQty: bQty
      };
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], ...normalized };
        return copy;
      }
      return [...prev, normalized];
    });
  };

  // Online Orders Mutator
  const handleApproveOrder = async (id) => {
    try {
      await fetch(`${API_BASE}/tenant/${activeSlug}/orders/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'APPROVED' })
      });
      setOnlineOrders(prev => prev.map(o => o.id === id ? { ...o, status: 'APPROVED' } : o));
    } catch (e) {
      console.error('Approve order error:', e);
    }
  };

  // ==========================================
  // VIEW 1: BRIGHT & FRESH LANDING PAGE
  // ==========================================
  if (routeState.view === 'statement-view') {
    return <PublicStatementView lang={lang} />;
  }
  if (routeState.view === 'invoice-view') {
    return <PublicInvoiceView lang={lang} />;
  }

  if (routeState.view === 'landing') {
    return (
      <LandingPage 
        lang={lang}
        onToggleLang={setLang}
        onNavigate={navigateTo}
      />
    );
  }

  // ==========================================
  // VIEW 2: DEDICATED TENANT LOGIN (NO CLIENT LEAKAGE)
  // ==========================================
  if (routeState.view === 'login') {
    return (
      <TenantLogin 
        lang={lang}
        slug={routeState.slug || 'samarth-krupa-dairy'}
        onLoginSuccess={handleTenantLoginSuccess}
        onNavigate={navigateTo}
      />
    );
  }

  // ==========================================
  // VIEW 3: DEVELOPER SUPER-ADMIN PORTAL (ISOLATED)
  // ==========================================
  if (routeState.view === 'developer') {
    return (
      <div style={{ minHeight: '100vh', background: '#0b0f19' }}>
        <Header
          currentTenant={currentTenant}
          shift={shift}
          onToggleShift={setShift}
          selectedDate={selectedDate}
          onChangeDate={setSelectedDate}
          currentRole="DEVELOPER"
          lang={lang}
          onToggleLang={setLang}
          onLogout={() => navigateTo('landing')}
        />
        <DeveloperPortal lang={lang} />
      </div>
    );
  }

  // ==========================================
  // VIEW 4: AUTHENTICATED DAIRY CLIENT APP
  // ==========================================
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', color: '#0f172a' }}>
      
      <PWAInstallPrompt lang={lang} currentTenant={currentTenant} />
      
      {/* 1. Collapsible Sidebar */}
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        currentRole={tenantUser?.role || 'ADMIN'}
        pendingOrdersCount={onlineOrders.filter(o => o.status === 'PENDING').length}
        lang={lang}
        features={tenantFeatures}
        isMobile={isMobile}
      />

      {/* 2. Main Content Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'hidden' }}>
        
        {/* Top Header */}
        <Header 
          currentTenant={{
            ...currentTenant,
            brandMarathi: currentTenant?.brand_name_mr || currentTenant?.name || 'समर्थ कृपा डेअरी',
            brandEnglish: currentTenant?.brand_name_en || currentTenant?.name || 'Samarth Krupa Dairy',
            city: currentTenant?.city || 'पुणे'
          }}
          shift={shift}
          onToggleShift={setShift}
          selectedDate={selectedDate}
          onChangeDate={setSelectedDate}
          currentRole={tenantUser?.role || 'ADMIN'}
          lang={lang}
          onToggleLang={setLang}
          onLogout={handleTenantLogout}
        />

        {/* Dynamic Page Views */}
        <main style={{ flex: 1, padding: (tenantUser?.role === 'DELIVERY' || (isMobile && activeTab === 'runsheet')) ? 0 : '0.75rem', paddingBottom: (isMobile && tenantUser?.role === 'ADMIN') ? '80px' : (tenantUser?.role === 'DELIVERY' ? '75px' : '20px'), overflowY: 'auto' }}>
          
          {dataLoading && (
            <div style={{ padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0369a1', fontSize: '0.78rem', fontWeight: 700, borderRadius: 8, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>डेटाबेसमधून थेट माहिती लोड होत आहे... (Syncing live database...)</span>
            </div>
          )}

          {activeTab === 'dashboard' && (
            <Dashboard 
              tenant={currentTenant}
              currentTenant={currentTenant}
              shift={shift}
              selectedDate={selectedDate}
              lines={lines}
              products={products}
              customers={customers}
              deliveriesToday={deliveriesToday}
              onlineOrders={onlineOrders}
              onNavigate={setActiveTab}
              lang={lang}
              features={tenantFeatures}
              currentUser={tenantUser}
            />
          )}

          {activeTab === 'pos' && (
            <CounterPOS 
              products={products}
              customers={customers}
              currentTenant={currentTenant}
              gstEnabled={gstEnabled}
              onToggleGst={() => setGstEnabled(!gstEnabled)}
              lang={lang}
            />
          )}

          {activeTab === 'runsheet' && (
            <DailyRunSheet 
              currentUser={tenantUser}
              onLogout={handleTenantLogout} 
              currentTenant={currentTenant}
              shift={shift}
              selectedDate={selectedDate}
              centers={centers}
              lines={lines}
              products={products}
              customers={customers}
              deliveries={deliveriesToday}
              onUpdateDelivery={handleUpdateDelivery}
              lang={lang}
              features={tenantFeatures}
            />
          )}

          {activeTab === 'customers' && (
            <CustomerManagement 
              currentTenant={currentTenant}
              customers={customers}
              centers={centers}
              lines={lines}
              onSaveCustomer={handleSaveCustomer}
              onDeleteCustomer={handleDeleteCustomer}
              onMarkVacation={handleMarkVacation}
              lang={lang}
            />
          )}

          {activeTab === 'rates' && (
            <div>
            {isMobile && (
              <button
                type="button"
                onClick={() => setActiveTab('more')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#ffffff', border: '1.5px solid #cbd5e1', padding: '6px 12px',
                  borderRadius: 8, fontSize: '0.78rem', fontWeight: 800, color: '#0284c7',
                  cursor: 'pointer', marginBottom: 10
                }}
              >
                <ArrowLeft size={14} />
                <span>{isMr ? 'अधिक मेनूवर परत' : 'Back to More'}</span>
              </button>
            )}
            <RateChartMaster 
              currentTenant={currentTenant}
              centers={centers}
              lines={lines}
              products={products}
              currentUser={tenantUser}
              lang={lang}
            />
            </div>
          )}

          {activeTab === 'products' && (
            <ProductCatalog 
              currentTenant={currentTenant}
              products={products}
              onSaveProduct={handleSaveProduct}
              onDeleteProduct={handleDeleteProduct}
              lang={lang}
              features={tenantFeatures}
            />
          )}

          {activeTab === 'lines' && (
            <LinesMaster 
              currentTenant={currentTenant}
              centers={centers}
              lines={lines}
              onSaveLine={handleSaveLine}
              onDeleteLine={handleDeleteLine}
              lang={lang}
            />
          )}

          {activeTab === 'billing' && (
            <BillingLedger 
              customers={customers}
              products={products}
              centers={centers}
              lines={lines}
              features={tenantFeatures}
              currentTenant={currentTenant}
              tenant={currentTenant}
              onNavigateToPayments={() => setActiveTab('payments')}
              lang={lang}
            />
          )}

                    {activeTab === 'payments' && (
            <CustomerPaymentReceipts 
              customers={customers}
              centers={centers}
              lines={lines}
              routes={lines}
              currentTenant={currentTenant}
              tenant={currentTenant}
              currentUser={tenantUser}
              user={tenantUser}
              lang={lang}
            />
          )}

          {activeTab === 'users' && (
            <UserManagement 
              centers={centers}
              lines={lines}
              lang={lang}
              currentTenant={currentTenant}
            />
          )}

          {activeTab === 'online_orders' && (
            <OnlineOrders 
              initialOrders={onlineOrders}
              onApproveOrder={handleApproveOrder}
              lang={lang}
            />
          )}

          {/* Mobile Admin More Hub */}
          {isMobile && activeTab === 'more' && (
            <div style={{ maxWidth: 540, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12, padding: '0.5rem 0.25rem' }}>
              <div style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', borderRadius: 16, padding: '16px 18px', color: '#ffffff', boxShadow: '0 4px 12px rgba(2,132,199,0.25)' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 900 }}>
                  {isMr ? '⚙️ अधिक डेअरी व्यवस्थापन मेनू' : '⚙️ More Dairy Management'}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#e0f2fe', marginTop: 4 }}>
                  {isMr ? 'दरपत्रक, मार्ग, कर्मचारी व बँक खात्याचा तपशील' : 'Rate charts, lines, staff & invoice bank details'}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {/* 0. पेमेंट पावती व वसुली */}
                <button
                  type="button"
                  onClick={() => setActiveTab('payments')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#ffffff', borderRadius: 14, padding: '14px 16px',
                    border: '1.5px solid #cbd5e1', cursor: 'pointer', textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                      <CreditCard size={22} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#0f172a' }}>
                        {isMr ? 'पेमेंट पावती व वसुली (Payment Receipts)' : 'Payment Receipts & Collection'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {isMr ? 'रोख, UPI व चेक पावती बनवा' : 'Record cash, UPI & cheque payments'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={18} color="#94a3b8" />
                </button>

                {/* 1. दूध दरपत्रक */}
                <button
                  type="button"
                  onClick={() => setActiveTab('rates')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#ffffff', borderRadius: 14, padding: '14px 16px',
                    border: '1.5px solid #cbd5e1', cursor: 'pointer', textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0284c7' }}>
                      <TrendingUp size={22} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#0f172a' }}>
                        {isMr ? 'दूध दरपत्रक (Milk Rates)' : 'Milk Rate Charts'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {isMr ? 'मार्गानिहाय गाय व म्हैस दुधाचे दर ठरवा' : 'Set route-wise milk rates'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={18} color="#94a3b8" />
                </button>

                {/* 2. मार्ग व्यवस्थापन */}
                <button
                  type="button"
                  onClick={() => setActiveTab('lines')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#ffffff', borderRadius: 14, padding: '14px 16px',
                    border: '1.5px solid #cbd5e1', cursor: 'pointer', textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                      <Navigation size={22} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#0f172a' }}>
                        {isMr ? 'लाईन्स व्यवस्थापन (Delivery Routes)' : 'Delivery Lines'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {isMr ? 'नवीन रतीब मार्ग व केंद्र जोडा' : 'Add & manage delivery routes'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={18} color="#94a3b8" />
                </button>

                {/* 3. कर्मचारी व हक्क */}
                <button
                  type="button"
                  onClick={() => setActiveTab('users')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#ffffff', borderRadius: 14, padding: '14px 16px',
                    border: '1.5px solid #cbd5e1', cursor: 'pointer', textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9333ea' }}>
                      <Users size={22} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#0f172a' }}>
                        {isMr ? 'कर्मचारी व हक्क (Staff Rights)' : 'Staff Management'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {isMr ? 'डिलिव्हरी बॉय व कॅशियर खाती व्यवस्थापित करा' : 'Manage delivery boy accounts'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={18} color="#94a3b8" />
                </button>

                {/* 4. डेअरी व बँक सेटिंग्ज */}
                <button
                  type="button"
                  onClick={() => setActiveTab('settings')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#ffffff', borderRadius: 14, padding: '14px 16px',
                    border: '1.5px solid #cbd5e1', cursor: 'pointer', textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
                      <Sliders size={22} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#0f172a' }}>
                        {isMr ? 'डेअरी प्रोफाईल व बँक तपशील' : 'Dairy & Bank Settings'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {isMr ? 'FSSAI, GST आणि बिलावरील बँक खात्याचा तपशील' : 'Configure invoice bank details'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={18} color="#94a3b8" />
                </button>

                {/* 5. सुरक्षित लॉगआउट */}
                <button
                  type="button"
                  onClick={handleTenantLogout}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    background: '#fef2f2', borderRadius: 14, padding: '14px 16px',
                    border: '1.5px solid #fecaca', cursor: 'pointer',
                    color: '#dc2626', fontWeight: 800, fontSize: '0.92rem', marginTop: 6
                  }}
                >
                  <LogOut size={18} />
                  <span>{isMr ? '🚪 डेअरी खात्यातून सुरक्षित लॉगआउट करा' : 'Sign Out from Dairy'}</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div style={{ maxWidth: 840, margin: '1rem auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              {/* Card 1: Dairy Profile & Business Settings */}
              <div style={{ background: '#ffffff', borderRadius: 14, padding: '1.5rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.35rem' }}>
                  🏢 {isMr ? 'डेअरी प्रोफाईल व बिलिंग प्रिंटिंग सेटिंग्ज' : 'Dairy Profile & Billing Print Settings'}
                </h2>
                <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '1.25rem' }}>
                  {isMr 
                    ? 'येथील माहिती थेट Tenant डेटाबेसमध्ये सुरक्षित सेव्ह होते आणि ग्राहकांच्या बिल पावतीवर व इन्व्हाईसवर आपोआप दिसते.' 
                    : 'These business details are saved directly in your Tenant DB and printed on customer milk bills and receipts.'}
                </p>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.target;
                  const upi_id = form.upi_id.value.trim();
                  const phone = form.phone.value.trim();
                  const address = form.address.value.trim();
                  const fssai_no = form.fssai_no.value.trim();
                  const gst_no = form.gst_no.value.trim();
                  const bank_name = form.bank_name.value.trim();
                  const bank_account_no = form.bank_account_no.value.trim();
                  const bank_ifsc = form.bank_ifsc.value.trim();
                  const bank_holder_name = form.bank_holder_name.value.trim();
                  const print_fssai_gst = form.print_fssai_gst.checked ? '1' : '0';
                  const print_bank_details = form.print_bank_details.checked ? '1' : '0';

                  try {
                    const targetSlug = currentTenant?.slug || activeSlug || (() => {
                      try {
                        const saved = localStorage.getItem('milkpro_tenant_data');
                        return saved ? JSON.parse(saved)?.slug : 'shreegajanan-dairy';
                      } catch {
                        return 'shreegajanan-dairy';
                      }
                    })();

                    const res = await fetch(`${API_BASE}/tenant/${targetSlug}/settings/update-profile`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ 
                        upi_id, 
                        phone, 
                        address,
                        fssai_no,
                        gst_no,
                        bank_name,
                        bank_account_no,
                        bank_ifsc,
                        bank_holder_name,
                        print_fssai_gst,
                        print_bank_details
                      })
                    });

                    const text = await res.text();
                    let d;
                    try {
                      d = JSON.parse(text);
                    } catch (parseErr) {
                      console.error('Server returned non-JSON:', text);
                      alert(isMr ? 'सर्व्हर एरर: प्रोफाईल सेव्ह करता आले नाही.' : 'Server error: unable to save profile.');
                      return;
                    }

                    if (d.success) {
                      const updatedSettings = d.settings || {
                        upi_id, phone, address, fssai_no, gst_no,
                        bank_name, bank_account_no, bank_ifsc, bank_holder_name,
                        print_fssai_gst, print_bank_details
                      };
                      setDairySettings(updatedSettings);

                      // Update currentTenant while safely preserving features, plan & permissions
                      setCurrentTenant(prev => {
                        const merged = {
                          ...prev,
                          ...(d.tenant || {}),
                          feature: (d.tenant && d.tenant.feature) ? d.tenant.feature : prev?.feature,
                          plan: (d.tenant && d.tenant.plan) ? d.tenant.plan : prev?.plan,
                          dairy_settings: updatedSettings
                        };
                        try {
                          localStorage.setItem('milkpro_tenant_data', JSON.stringify(merged));
                        } catch {}
                        return merged;
                      });

                      alert(isMr ? '✅ डेअरी प्रोफाईल व बिलिंग सेटिंग्ज यशस्वीरित्या सेव्ह झाल्या!' : 'Profile & billing settings saved successfully!');
                    } else {
                      alert(d.message || 'Error updating profile');
                    }
                  } catch (err) {
                    alert('Error: ' + err.message);
                  }
                }}>
                  {/* Section A: Basic Dairy Information */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155' }}>
                          {isMr ? 'डेअरीचे नाव' : 'Dairy Brand Name'}
                        </label>
                        <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          🔒 {isMr ? 'फक्त Developer बदलू शकतात' : 'Locked for Client'}
                        </span>
                      </div>
                      <input 
                        name="brand_name_mr"
                        value={currentTenant?.brand_name_mr || currentTenant?.brandMarathi || currentTenant?.name || ''}
                        disabled
                        readOnly
                        title={isMr ? 'डेअरीचे नाव सुरक्षित असून केवळ Developer द्वारे बदलता येते' : 'Dairy Name is protected and can only be changed by Developer'}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 800, cursor: 'not-allowed' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        {isMr ? 'संपर्क फोन / व्हॉट्सॲप नंबर' : 'Helpline / Contact Phone'}
                      </label>
                      <input 
                        name="phone"
                        defaultValue={dairySettings?.phone || currentTenant?.phone || ''}
                        placeholder="उदा. 9822012345"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                        {isMr ? 'डेअरीचा पत्ता (बिलावर दिसेल)' : 'Dairy Address (Printed on Bills)'}
                      </label>
                      <input 
                        name="address"
                        defaultValue={dairySettings?.address || currentTenant?.address || ''}
                        placeholder={isMr ? 'उदा. मु. पो. बारामती, जि. पुणे' : 'e.g. Baramati, Pune'}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#0369a1', marginBottom: 4 }}>
                        📲 {isMr ? 'डेअरीचा थेट UPI ID (QR कोड व पेमेंटसाठी)' : 'Dairy UPI ID (For Invoices & QR)'}
                      </label>
                      <input 
                        name="upi_id"
                        defaultValue={dairySettings?.upi_id || currentTenant?.upi_id || ''}
                        placeholder="उदा. yourname@okhdfcbank / phone@upi"
                        style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1.5px solid #0284c7', fontFamily: 'monospace', fontWeight: 800, fontSize: '0.95rem' }}
                      />
                    </div>
                  </div>

                  {/* Section B: FSSAI License & GSTIN */}
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>🛡️</span>
                      <span>{isMr ? 'अन्न परवाना व कर तपशील (FSSAI व GSTIN)' : 'Government License & Tax Details'}</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          {isMr ? 'FSSAI अन्न परवाना क्र. (Food License No)' : 'FSSAI License No'}
                        </label>
                        <input 
                          name="fssai_no"
                          defaultValue={dairySettings?.fssai_no || ''}
                          placeholder="उदा. 11524999000123"
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          {isMr ? 'GSTIN क्रमांक (GST No)' : 'GSTIN Number'}
                        </label>
                        <input 
                          name="gst_no"
                          defaultValue={dairySettings?.gst_no || ''}
                          placeholder="उदा. 27AAAAA0000A1Z5"
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontFamily: 'monospace', textTransform: 'uppercase' }}
                        />
                      </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#1e293b', fontWeight: 600, cursor: 'pointer' }}>
                      <input 
                        type="checkbox"
                        name="print_fssai_gst"
                        defaultChecked={dairySettings?.print_fssai_gst !== '0'}
                        style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                      />
                      <span>{isMr ? 'ग्राहक बिल पावतीवर FSSAI व GST क्रमांक प्रिंट करा' : 'Print FSSAI & GSTIN on customer milk bills'}</span>
                    </label>
                  </div>

                  {/* Section C: Bank Account Details for Bill Printing */}
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>🏦</span>
                      <span>{isMr ? 'बँक खात्याचा तपशील (बिल प्रिंटिंगसाठी)' : 'Bank Account Details (For Customer Invoices)'}</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          {isMr ? 'बँकेचे नाव (Bank Name)' : 'Bank Name'}
                        </label>
                        <input 
                          name="bank_name"
                          defaultValue={dairySettings?.bank_name || ''}
                          placeholder={isMr ? 'उदा. State Bank of India' : 'e.g. HDFC Bank'}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          {isMr ? 'खाते क्रमांक (Account Number)' : 'Account Number'}
                        </label>
                        <input 
                          name="bank_account_no"
                          defaultValue={dairySettings?.bank_account_no || ''}
                          placeholder="उदा. 34567890123"
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          {isMr ? 'IFSC कोड (IFSC Code)' : 'IFSC Code'}
                        </label>
                        <input 
                          name="bank_ifsc"
                          defaultValue={dairySettings?.bank_ifsc || ''}
                          placeholder="उदा. SBIN0001234"
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontFamily: 'monospace', textTransform: 'uppercase' }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                          {isMr ? 'खातेदाराचे नाव (A/C Holder Name)' : 'Account Holder Name'}
                        </label>
                        <input 
                          name="bank_holder_name"
                          defaultValue={dairySettings?.bank_holder_name || ''}
                          placeholder={isMr ? 'उदा. सचिन मिल्क शॉप' : 'e.g. Sachin Milk Shop'}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                        />
                      </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#1e293b', fontWeight: 600, cursor: 'pointer' }}>
                      <input 
                        type="checkbox"
                        name="print_bank_details"
                        defaultChecked={dairySettings?.print_bank_details === '1'}
                        style={{ width: 16, height: 16, accentColor: '#0284c7' }}
                      />
                      <span>{isMr ? 'ग्राहक बिल पावतीवर बँक तपशील (बँक नाव, A/C No, IFSC) प्रिंट करा' : 'Print Bank Account Details on customer milk bills'}</span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '0.65rem 2rem', borderRadius: 8, fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(22,163,74,0.2)' }}
                  >
                    {isMr ? '💾 सेटिंग्ज सेव्ह करा' : '💾 Save Settings'}
                  </button>
                </form>
              </div>

              {/* Card 2: System & Language Settings */}
              <div style={{ background: '#ffffff', borderRadius: 14, padding: '1.5rem', border: '1px solid #e2e8f0' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                {isMr ? 'प्रणाली व भाषा सेटिंग्ज' : 'System & Language Settings'}
              </h2>
              <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{isMr ? 'भाषा बदला' : 'Language Selection'}</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{isMr ? 'मराठी किंवा शुद्ध इंग्रजी' : 'Marathi or English'}</div>
                  </div>
                  <button 
                    onClick={() => setLang(lang === 'mr' ? 'en' : 'mr')}
                    style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                  >
                    {isMr ? 'English करा' : 'मराठी करा'}
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '1rem' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{isMr ? 'लॉगआउट' : 'Sign Out'}</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{isMr ? 'डेअरी खात्यातून सुरक्षित बाहेर पडा' : 'Sign out from this session'}</div>
                  </div>
                  <button 
                    onClick={handleTenantLogout}
                    style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                  >
                    {isMr ? 'लॉगआउट करा' : 'Logout'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}

        </main>
      </div>
      {/* ADMIN MOBILE BOTTOM NAVIGATION BAR (5 CATCHY VIBRANT BUTTONS) */}
      {isMobile && tenantUser?.role === 'ADMIN' && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 64,
          background: '#ffffff',
          borderTop: '1.5px solid #cbd5e1',
          boxShadow: '0 -4px 14px rgba(0,0,0,0.08)',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          zIndex: 9999,
          padding: '0 4px max(env(safe-area-inset-bottom, 0px), 4px) 4px'
        }}>
          {/* 1. डॅशबोर्ड (Dashboard) */}
          <button
            type="button"
            onClick={() => setActiveTab('dashboard')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: activeTab === 'dashboard' ? '1.5px solid #c7d2fe' : '1.5px solid transparent',
              background: activeTab === 'dashboard' ? '#e0e7ff' : 'transparent',
              borderRadius: 10,
              cursor: 'pointer',
              padding: '5px 2px',
              color: activeTab === 'dashboard' ? '#4338ca' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <LayoutDashboard size={18} color={activeTab === 'dashboard' ? '#4338ca' : '#64748b'} />
            <span style={{ fontSize: '0.68rem', fontWeight: activeTab === 'dashboard' ? 900 : 600, marginTop: 2 }}>
              {isMr ? 'डॅशबोर्ड' : 'Dashboard'}
            </span>
          </button>

          {/* 2. वाटप (Runsheet) */}
          <button
            type="button"
            onClick={() => setActiveTab('runsheet')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: activeTab === 'runsheet' ? '1.5px solid #bae6fd' : '1.5px solid transparent',
              background: activeTab === 'runsheet' ? '#e0f2fe' : 'transparent',
              borderRadius: 10,
              cursor: 'pointer',
              padding: '5px 2px',
              color: activeTab === 'runsheet' ? '#0284c7' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <Truck size={18} color={activeTab === 'runsheet' ? '#0284c7' : '#64748b'} />
            <span style={{ fontSize: '0.68rem', fontWeight: activeTab === 'runsheet' ? 900 : 600, marginTop: 2 }}>
              {isMr ? 'वाटप' : 'Delivery'}
            </span>
          </button>

          {/* 3. ग्राहक (Customers) */}
          <button
            type="button"
            onClick={() => setActiveTab('customers')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: activeTab === 'customers' ? '1.5px solid #bbf7d0' : '1.5px solid transparent',
              background: activeTab === 'customers' ? '#dcfce7' : 'transparent',
              borderRadius: 10,
              cursor: 'pointer',
              padding: '5px 2px',
              color: activeTab === 'customers' ? '#15803d' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <Users size={18} color={activeTab === 'customers' ? '#15803d' : '#64748b'} />
            <span style={{ fontSize: '0.68rem', fontWeight: activeTab === 'customers' ? 900 : 600, marginTop: 2 }}>
              {isMr ? 'ग्राहक' : 'Customers'}
            </span>
          </button>

          {/* 4. बिलिंग (Billing) */}
          <button
            type="button"
            onClick={() => setActiveTab('billing')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: activeTab === 'billing' ? '1.5px solid #fde68a' : '1.5px solid transparent',
              background: activeTab === 'billing' ? '#fef3c7' : 'transparent',
              borderRadius: 10,
              cursor: 'pointer',
              padding: '5px 2px',
              color: activeTab === 'billing' ? '#b45309' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <Receipt size={18} color={activeTab === 'billing' ? '#b45309' : '#64748b'} />
            <span style={{ fontSize: '0.68rem', fontWeight: activeTab === 'billing' ? 900 : 600, marginTop: 2 }}>
              {isMr ? 'बिलिंग' : 'Billing'}
            </span>
          </button>

          {/* 5. अधिक (More) */}
          <button
            type="button"
            onClick={() => setActiveTab('more')}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: (activeTab === 'more' || activeTab === 'settings' || activeTab === 'rates' || activeTab === 'lines' || activeTab === 'users') ? '1.5px solid #cbd5e1' : '1.5px solid transparent',
              background: (activeTab === 'more' || activeTab === 'settings' || activeTab === 'rates' || activeTab === 'lines' || activeTab === 'users') ? '#f1f5f9' : 'transparent',
              borderRadius: 10,
              cursor: 'pointer',
              padding: '5px 2px',
              color: (activeTab === 'more' || activeTab === 'settings' || activeTab === 'rates' || activeTab === 'lines' || activeTab === 'users') ? '#334155' : '#64748b',
              transition: 'all 0.15s ease'
            }}
          >
            <MoreHorizontal size={18} color={(activeTab === 'more' || activeTab === 'settings' || activeTab === 'rates' || activeTab === 'lines' || activeTab === 'users') ? '#334155' : '#64748b'} />
            <span style={{ fontSize: '0.68rem', fontWeight: (activeTab === 'more' || activeTab === 'settings' || activeTab === 'rates' || activeTab === 'lines' || activeTab === 'users') ? 900 : 600, marginTop: 2 }}>
              {isMr ? 'अधिक' : 'More'}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
